
/**
 * MailTemplate Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';
import { MailTemplateFormSchema } from '@repo/contracts';

export class MailTemplateService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            const body = response;
            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                message: body.message || 'MailTemplate listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch mail-template list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch mail-template');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        // Validate with Zod
        const validation = MailTemplateFormSchema.safeParse(data);
        if (!validation.success) {
            const errorMessage = validation.error.errors[0].message;
            throw new ApiError(errorMessage, 400, 'VALIDATION_ERROR');
        }

        try {
            const response = await this.api.create(validation.data);

            return {
                success: true,
                data: response.data,
                message: response.message || 'MailTemplate oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create mail-template');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        // Validate with Zod
        const validation = MailTemplateFormSchema.partial().safeParse(data);
        if (!validation.success) {
            const errorMessage = validation.error.errors[0].message;
            throw new ApiError(errorMessage, 400, 'VALIDATION_ERROR');
        }

        try {
            const response = await this.api.update(id, validation.data);

            return {
                success: true,
                data: response.data,
                message: response.message || 'MailTemplate güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update mail-template');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'MailTemplate silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete mail-template');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);

            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk delete mail-templates');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
